import { TriangleAlert } from 'lucide-react'
import { useEffect } from 'react'

import { NEGATIVE_OUTCOME_STAGES, type QuoteCardStage } from './quoteCardTypes'
import { QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'

/**
 * Whether every carrier that answered came back declined, errored or unavailable — and
 * none are still out. Takes every indication's stage rather than the rendered cards: a
 * customer-hidden Rocklake decline leaves no card, and reading the cards alone would call
 * an all-Rocklake decline "no carriers yet". No indications at all isn't this state either.
 */
export function allCarriersDeclined(stages: QuoteCardStage[], awaitingCarrierCount: number): boolean {
  if (awaitingCarrierCount > 0 || stages.length === 0) return false
  return stages.every((stage) => NEGATIVE_OUTCOME_STAGES.has(stage))
}

/**
 * Covers the lower half of the page once no carrier has priced the risk, with nothing to
 * dismiss it: the cards underneath are all dead ends, so the broker picking the application
 * up is the only thing left to read.
 */
export function AllDeclinedOverlay() {
  const track = useTrackQuotesEvent()

  useEffect(() => {
    track(QuotesEvent.ALL_DECLINED_SHOWN)
  }, [track])

  return (
    // Sticky off a zero-height anchor, not fixed: rides the viewport bottom but stops before the footer.
    <div className="sticky bottom-0 z-30 h-0">
      <div
        role="status"
        className="from-background via-background/95 absolute inset-x-0 bottom-0 flex h-[55vh] flex-col items-center justify-center bg-linear-to-t via-40% to-transparent px-6 text-center"
      >
        {/* Off the scrim's centre to clear the taller desktop cards, leaving the fade where it is. */}
        <div className="flex flex-col items-center md:translate-y-16">
          <TriangleAlert aria-hidden="true" className="mb-4 size-6 text-(--amber)" />
          <h2 className="text-ink max-w-[28ch] text-2xl font-bold tracking-[-0.01em] text-balance">
            An atypical number of insurance providers have declined.
          </h2>
          <p className="text-ink-500 text-md mt-3 max-w-md leading-relaxed">
            Your matched broker will take a look at your application, and be in touch within 24 hours.
          </p>
        </div>
      </div>
    </div>
  )
}
